import { Info } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * What each targeting section does, written against the runtime's own matching
 * (`helpers/url-match.ts`, `injector/cookie-target-match.ts`, `run-experiments.ts`), so the
 * words and the behaviour cannot drift apart without one of those files changing too.
 */
export const TARGETING_HELP = {
  overview:
    'A visitor sees the experiment only when every section that has rules is satisfied. Inside the URL, cookie and device sections one matching rule is enough. Inside Activation every rule must be met.',
  url: 'Runs the experiment only on pages whose full address, query string included, matches at least one of these rules. A trailing slash is ignored. With no rules the experiment runs on every page.',
  cookie:
    "Runs the experiment only for visitors whose browser cookies contain at least one of these values. The text is looked for anywhere in the visitor's cookies, not in one named cookie. With no values every visitor qualifies.",
  device:
    'Runs the experiment only on the selected device types. With no rule it runs on all devices. "All" replaces any specific choice.',
  activation:
    'Holds the experiment back until the site gives a signal, either an event it sends or an element that is on the page. Every activation rule must be met. An event stays true for that visitor once it has been sent, while a selector is checked again on each page. With no rules the experiment is not held back.',
} as const;

type HelpTipProps = { text: string; label: string; className?: string };

/** A small info icon that explains a targeting section. Reachable by keyboard as well as hover. */
export const HelpTip = ({ text, label, className }: HelpTipProps) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`About ${label}`}
          className={cn(
            'text-dim hover:text-foreground focus-visible:ring-ring flex size-4 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:ring-1 focus-visible:outline-none',
            className,
          )}
        >
          <Info className="size-3.5" strokeWidth={1.5} />
        </button>
      </TooltipTrigger>
      <TooltipContent className="border-line bg-panel text-foreground rounded-lg">
        <p className="supporting max-w-xs">{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);
